import { ApexOptions } from 'apexcharts';
import { ComponentType, Suspense, lazy, useMemo } from 'react';
import type { Props as ReactApexChartProps } from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import { goodFormatNumber } from '../utils';
import { Spinner } from './Spinner';

const Chart = lazy(() =>
  import('react-apexcharts').then(module => ({
    default: ((module as any).default?.default ?? (module as any).default ?? module) as any,
  })),
) as unknown as ComponentType<ReactApexChartProps>;

export interface ProgressChartItem {
  name: string;
  value: number;
  valueName?: string;
  percentage?: number;
}

export interface ProgressChartProps {
  colors?: string[];
  items: ProgressChartItem[];
}

export const ProgressChart = ({ colors, items }: ProgressChartProps) => {
  const options = useMemo<ApexOptions>(
    () => ({
      chart: { type: 'bar', stacked: true, sparkline: { enabled: true }, width: '100%' },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '10px',
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
          borderRadius: 5,
        },
      },
      colors: items.map(
        (_, index) =>
          (colors?.length ? colors : ['#1E6091', '#48CAE4', '#56C596', '#F4C430', '#B276E2'])[
            index % (colors?.length || 5)
          ],
      ),
      tooltip: {
        enabled: true,
        followCursor: true,
        custom: ({ seriesIndex }) => {
          const item = items[seriesIndex];
          return renderToString(
            <div className="flex items-center gap-2 font-[500] p-2">
              <span
                className="block w-[6px] h-[6px] min-h-[6px] min-w-[6px] rounded-full"
                style={{
                  background: colors?.length ? colors[seriesIndex % colors.length] : '#1E6091',
                }}
              ></span>
              <span>
                {' '}
                {item?.name ?? ''}: {goodFormatNumber(item?.value)}
                {item?.valueName || ''}{' '}
                {item?.percentage ? `(${goodFormatNumber(item?.percentage)}%)` : ''}
              </span>
            </div>,
          );
        },
      },
      xaxis: { categories: ['Progress'] },
      dataLabels: { enabled: false },
      grid: { show: false },
      stroke: { show: true, width: 0, colors: ['#fff'] },
    }),
    [colors, items],
  );

  const series = useMemo<ApexAxisChartSeries | ApexNonAxisChartSeries>(() => {
    return (
      items?.map(item => ({
        name: item.name,
        data: [item.value],
      })) || [
        { name: 'Segment 1', data: [30] },
        { name: 'Segment 2', data: [20] },
        { name: 'Segment 3', data: [10] },
        { name: 'Segment 4', data: [15] },
        { name: 'Segment 5', data: [25] },
      ]
    );
  }, [items]);

  return (
    <Suspense
      fallback={
        <div className="min-h-[250px] h-full w-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      }
    >
      <Chart options={options} series={series} type="bar" height={20} />
    </Suspense>
  );
};
